import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { useAppContext } from "../../../Contexts/StateProvider";
import ReactSelect from "react-select";
import { ResponsiveImage, Heading } from "../..";
import communeGeo from "./communeGeo.json";

const Root = styled.section`
  overflow: hidden;
  position: relative;

  &:after {
    background-color: ${(props) => props.theme.colors.bgColor};
    background-image: url('data:image/svg+xml;utf8,<svg width="15" height="25" xmlns="http://www.w3.org/2000/svg"><path d="M13.81 13.353c.476-.48.476-1.253 0-1.706L3.136.9a1.336 1.336 0 0 0-1.906 0 1.359 1.359 0 0 0 0 1.92l8.765 8.827c.477.48.477 1.253 0 1.706L1.23 22.18a1.359 1.359 0 0 0 0 1.92c.53.533 1.377.533 1.906 0L13.81 13.353Z" fill="%238ECDB2" fill-rule="nonzero"/></svg>');
    background-repeat: no-repeat;
    background-position: center;
    background-size: 20px 35px;
    border-radius: 100%;
    bottom: 0;
    content: "";
    display: block;
    height: 120px;
    left: 50%;
    position: absolute;
    transform: translate3d(-50%, 25%, 0) rotate(90deg);
    width: 120px;
    z-index: 1000;
  }
`;
const Select = styled.div`
  height: 100%;
  width: 100%;
  text-align: center;
`;

const MapContainer = styled.div`
  background-color: #cad2d3;
  height: 620px;
  margin: 0 0 30px;
  width: 100%;
  z-index: 0;

  .leaflet-bar {
    border-color: ${(props) => props.theme.colors.neoMint};
  }

  .leaflet-control-zoom {
    a {
      color: ${(props) => props.theme.colors.neoMint};
      border-color: ${(props) => props.theme.colors.neoMint};
    }
    .leaflet-disabled {
      color: RGBA(142, 205, 178, 0);
    }
  }

  .leaflet-popup-content-wrapper {
    border-radius: 0;
  }

  .smallMarker {
    padding: 10px;
    svg {
      display: block;
    }
  }

  a.leaflet-popup-close-button {
    color: ${(props) => props.theme.colors.textColor};
    font-size: 24px !important;
    padding: 0;
    right: 10px !important;
    top: 10px !important;
  }

  .activityMapPopup {
    display: flex;
    flex-direction: column;
    font-family: "Avenir";
    font-size: 15px;
    padding: 10px 20px;
    text-align: center;
    width: 279px;

    img {
      max-width: 240px;
      max-height: 120px;
      margin: 0 auto;
      width: auto;
    }

    h3 {
      font-size: 20px;
      line-height: 28px;
      margin: 7px 0;
    }

    p {
      background-image: linear-gradient(
        ${(props) => props.theme.navigation.lineColor},
        ${(props) => props.theme.navigation.lineColor}
      );
      background-position: 50% 0%;
      background-repeat: no-repeat;
      background-size: 200px 1px;
      margin-top: 0;
      padding-top: 15px;
    }

    .activityMapPopupButton {
      align-items: center;
      background-color: ${(props) => props.theme.colors.neoMint};
      border-radius: 38px;
      color: ${(props) => props.theme.colors.neutralWhite};
      display: inline-flex;
      cursor: pointer;
      font-weight: 600;
      height: 50px;
      justify-content: center;
      min-width: 200px;
      padding: 0 25px;
      text-decoration: none;
      transform: scale(1);
      transition: transform 100ms ease-in-out;
      will-change: transform;
      width: auto;
      -webkit-tap-highlight-color: transparent;

      &:hover {
        transform: scale(1.035);
      }

      svg {
        margin-left: 16px;
      }
    }
  }
`;

const CommuneFilter = styled.div`
  margin: 0 auto;
  max-width: 330px;
`;

const CommuneContainer = styled.div`
  margin: 0 auto;
  max-width: 470px;
`;

const CommuneLogo = styled.div`
  background-color: white;
  border-radius: 100%;
  height: 98px;
  margin: 50px auto 13px;
  position: relative;
  transition: all 0.3s ease;
  width: 98px;

  svg,
  img {
    height: 100%;
    max-width: 65px;
    max-height: 65px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: auto;
  }

  svg {
    height: auto;
  }
`;

const selectStyles = {
  menu: (provided, state) => ({
    ...provided,
    width: "100%",
    color: state.selectProps.menuColor,
    borderRadius: "8px",
    padding: 0,
    zIndex: 3000,
    cursor: "pointer",
  }),
  menuList: (provided) => ({
    ...provided,
    borderRadius: "8px",
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    whiteSpace: "nowrap",
    fontFamily: "Avenir",
    color: "#939393",
  }),
  input: (provided) => ({
    ...provided,
    color: "#4A4A4A",
  }),
  control: (provided) => ({
    ...provided,
    backgroundColor: "#fff",
    border: "1px solid #BCB8B1",
    fontSize: "14px",
    fontWeight: "normal",
    lineHeight: "48px",
    height: "48px",
    cursor: "pointer",
    boxShadow: "0 !important",
    paddingLeft: "16px",
    "&:hover": {
      border: "1px solid #BCB8B1 !important",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "14px",
    fontWeight: "normal",
    lineHeight: "48px",
    display: "flex",
    alignItems: "center",
    letterSpacing: "normal",
    color: "#4A4A4A",
    background: state.isSelected ? "#f4f4f4" : "#fff",
    padding: "0 8px",
    height: "48px",
    maxHeight: "48px",
    minHeight: "32px",
    borderBottom: "1px solid #BCB8B1",
    paddingLeft: "16px",

    "&:hover": {
      background: "#f4f4f4",
      "& path": {
        stroke: "#fff",
      },
    },

    "&:last-of-type": {
      borderBottom: 0,
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    color: "#fff",
    padding: "0",
    height: "48px",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    "& > div": {
      borderLeft: "1px solid #BCB8B1",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "#4A4A4A",
  }),
};

const ActivityMap = ({ Configuration, Communes, Hosts }) => {
  const filtersRef = useRef<any>();
  const { CurrentCommune, setCurrentCommune } = useAppContext();
  const { map, setMap } = useAppContext();
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);

  //Indlæser kort ved første load eller når map eller setMap ændre sig
  useEffect(() => {
    const initMap = async () => {
      if (typeof window !== "undefined") {
        const L = (await import("leaflet")).default;

        if (!map) {
          const newMap = L.map("map", {
            center: [56.26, 11], // Danmarks center
            zoom: 7,
            zoomControl: true,
          });

          // Tilføjer minimalistisk kort
          L.tileLayer(
            "https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiYmFsY2hlbnBvc3QiLCJhIjoiY2tqaWJ1bXRsMmd4MjMxc2NobWd3Nnk2dyJ9.1IiEC7KCRVz1sQsnA562Yw",
            {
              minZoom: 7,
              maxZoom: 18,
              id: "mapbox/light-v9",
              tileSize: 512,
              zoomOffset: -1,
            }
          ).addTo(newMap);

          setMap(newMap);
        }
      }
    };

    initMap();
  }, [map, setMap]);

  useEffect(() => {
    const addDataToMap = async () => {
      if (typeof window !== "undefined" && map) {
        const L = (await import("leaflet")).default;

        const defaultStyle = {
          color: "#8ECDB2",
          weight: 1.5,
          fillOpacity: 0.2,
        };

        const hoverStyle = {
          color: "#8ECDB2",
          fillOpacity: 0.5,
        };
        const activeStyle = {
          color: "#8ECDB2",
          weight: 2,
          fillOpacity: 0.5,
        };

        const onEachFeature = (feature, layer) => {
          // Nulstil stil til defaultStyle
          layer.setStyle(defaultStyle);

          // Anvend hoverStyle hvis feature svarer til CurrentCommune
          if (feature.properties.LAU_CODE === CurrentCommune) {
            layer.setStyle(activeStyle);
          }

          layer.on({
            mouseover: (e) => {
              const hoveredLayer = e.target;

              if (hoveredLayer instanceof L.Path) {
                hoveredLayer.setStyle(hoverStyle);
              }

              hoveredLayer
                .bindTooltip(feature.properties.LAU_NAME, {
                  permanent: false,
                  direction: "top",
                  className: "custom-tooltip",
                })
                .openTooltip();
            },
            mouseout: (e) => {
              const hoveredLayer = e.target;

              if (hoveredLayer instanceof L.Path) {
                // Tjek om det er CurrentCommune før du ændrer stil
                if (feature.properties.LAU_CODE !== CurrentCommune) {
                  hoveredLayer.setStyle(defaultStyle);
                }
              }

              // Fjern tooltip ved mouseout
              hoveredLayer.unbindTooltip();
            },
            click: () => {
              map.fitBounds(layer.getBounds());
              setCurrentCommune(feature.properties.LAU_CODE);
            },
          });
        };

        const activeCommunes = {
          type: "FeatureCollection" as const,
          features: communeGeo.filter((commune) =>
            Communes.map((c) => c.CommuneId).includes(
              commune.properties.LAU_CODE
            )
          ),
        };

        // Fjern eksisterende lag
        map.eachLayer((layer) => {
          if (layer instanceof L.GeoJSON) {
            map.removeLayer(layer);
          }
        });

        // Tegner kommuner med activeCommunes state
        L.geoJSON(activeCommunes, {
          style: defaultStyle,
          onEachFeature,
        }).addTo(map);
      }
    };

    addDataToMap();
  }, [map, setCurrentCommune, Hosts, CurrentCommune]);

  //Tilføjer markers baseret på zoom og område og tilføjer dem først når de er inden for det givne område.
  useEffect(() => {
    const addHostsToMap = async () => {
      if (typeof window !== "undefined" && map) {
        const L = (await import("leaflet")).default;

        // Definer icons
        const svgIcon = L.divIcon({
          html: `
            <svg width="9" height="9" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="9" r="9" fill="#7AB59C" />
            </svg>`,
          className: "smallMarker",
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        });

        const resizedIcon = L.divIcon({
          html: `<svg width="61" height="55" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5 0C25.598 0 33 7.397 33 16.489 33 25.198 22.363 41.18 16.499 49 10.634 41.192 0 25.226 0 16.489 0 7.397 7.402 0 16.5 0Zm0 9a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17Z" fill="#7AB59C"/>
          </svg>`,
          className: "largeMarker",
          iconSize: [61, 55],
          iconAnchor: [16, 33],
        });

        const fetchHosts = async () => {
          const bounds = map.getBounds();
          const currentZoom = map.getZoom();

          // Fjern alle markers og popups hvis zoom er under 9
          if (currentZoom < 9) {
            map.eachLayer((layer) => {
              if (layer instanceof L.Marker || layer instanceof L.Popup) {
                map.removeLayer(layer);
              }
            });
            return;
          }

          try {
            const response = await fetch(
              `${
                Configuration.FetchHostsUrl
              }?minLat=${bounds.getSouth()}&maxLat=${bounds.getNorth()}&minLon=${bounds.getWest()}&maxLon=${bounds.getEast()}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            if (!response.ok) {
              throw new Error(`Failed to fetch hosts`);
            }

            const hosts = await response.json();

            // Fjern eksisterende markers
            map.eachLayer((layer) => {
              if (layer instanceof L.Marker) {
                map.removeLayer(layer);
              }
            });

            // Tilføj nye markers
            hosts.forEach((host) => {
              const marker = L.marker(host.Marker, {
                icon: currentZoom > 11 ? resizedIcon : svgIcon,
              });

              marker.on("click", function (e) {
                // Luk alle eksisterende popups
                map.eachLayer((layer) => {
                  if (layer instanceof L.Popup) {
                    map.removeLayer(layer);
                  }
                });

                // Opret popup separat
                const popup = L.popup({
                  closeButton: true,
                  autoClose: false,
                  closeOnClick: false,
                  closeOnEscapeKey: true,
                  autoPan: false,
                  className: "custom-popup",
                }).setContent(`
                  <div class="activityMapPopup">
                    <img src="${host.Logo.ImageUrl}" alt="${host.HostName}" />
                    <h3>${host.HostName}</h3>
                    <p>${host.HostDescription}</p>
                    <div><a href="#searchSection" class="activityMapPopupButton">
                        <span>${
                          Configuration?.HostPopup?.ButtonLabel || ""
                        }</span>
                        <svg focusable="false" viewBox="0 0 15 18" aria-hidden="true" role="img" width="18px" height="18px" class="sc-gzVnrw hcmybO"><path d="M7.402.556c-.592 0-1.071.48-1.071 1.071v11.754L2.269 9.025a1.072 1.072 0 1 0-1.565 1.463l5.913 6.347a1.072 1.072 0 0 0 1.568 0l5.915-6.347a1.072 1.072 0 1 0-1.567-1.463l-4.06 4.356V1.627c0-.591-.479-1.071-1.07-1.071Z" fill="CurrentColor" fill-rule="nonzero"></path></svg>
                      </a></div>
                  </div>
                `);

                const latlng = this.getLatLng();
                const latHeight =
                  map.getBounds().getNorthEast().lat -
                  map.getBounds().getSouthWest().lat;

                // Deaktiver event listeners midlertidigt
                map.off("movestart");
                map.off("zoomstart");

                // Åbn popup og flyt kortet
                popup.setLatLng(latlng).openOn(map);

                map.flyTo(
                  [latlng.lat + latHeight / 3, latlng.lng],
                  map.getZoom(),
                  {
                    duration: 0.5,
                    noMoveStart: true,
                  }
                );

                // Genaktiver event listeners efter flyTo er færdig
                map.once("moveend", () => {
                  map.on("movestart", () => {
                    map.eachLayer((layer) => {
                      if (layer instanceof L.Popup) {
                        map.removeLayer(layer);
                      }
                    });
                  });

                  map.on("zoomstart", () => {
                    map.eachLayer((layer) => {
                      if (layer instanceof L.Popup) {
                        map.removeLayer(layer);
                      }
                    });
                  });
                });
              });

              marker.addTo(map);
            });
          } catch (error) {
            console.error("Error fetching hosts:", error);
          }
        };

        // Lyt efter map events
        map.on("moveend", fetchHosts);
        map.on("zoomend", fetchHosts);

        // Initial fetch
        fetchHosts();

        // Cleanup
        return () => {
          map.off("moveend", fetchHosts);
          map.off("zoomend", fetchHosts);
        };
      }
    };

    addHostsToMap();
  }, [map]);

  const selectOptions = [
    ...Communes.map(({ CommuneId: value, CommuneName: label }) => {
      return {
        value,
        label,
      };
    }),
  ];

  const handleCommuneChange = (selectedOption) => {
    const communeId = selectedOption.value;
    setCurrentCommune(communeId);

    map.eachLayer((layer) => {
      if (layer.feature?.properties?.LAU_CODE === communeId) {
        map.fitBounds(layer.getBounds());
      }
    });
  };

  const getCommuneLogo = () => {
    if (CurrentCommune && Communes) {
      const result = Communes.find(
        (commune) => commune.CommuneId === CurrentCommune
      );
      return result?.CommuneLogo;
    }
    return null;
  };

  const getCommuneName = () => {
    if (CurrentCommune && Communes) {
      const result = Communes.find(
        (commune) => commune.CommuneId === CurrentCommune
      );
      return result?.CommuneName || "";
    }
    return "";
  };

  return (
    <div>
      <Root>
        <Helmet>
          <link
            rel="stylesheet"
            href="https://unpkg.com/leaflet/dist/leaflet.css"
          />
        </Helmet>
        <MapContainer id="map" />
      </Root>
      <Select>
        <CommuneFilter ref={filtersRef}>
          <ReactSelect
            instanceId="commune-select"
            value={
              menuIsOpen
                ? null
                : selectOptions.filter((option) => {
                    return option.value === CurrentCommune;
                  })
            }
            onChange={(option) => {
              handleCommuneChange(option);
              setMenuIsOpen(false);
            }}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}
            menuIsOpen={menuIsOpen}
            placeholder={menuIsOpen ? "" : Configuration.Filters.Commune.Label}
            options={selectOptions}
            styles={selectStyles}
            isSearchable={true}
            noOptionsMessage={() => "Ingen kommuner fundet"}
          />
        </CommuneFilter>
        {CurrentCommune && getCommuneLogo() && (
          <CommuneContainer>
            <CommuneLogo>
              <ResponsiveImage {...getCommuneLogo()}></ResponsiveImage>
            </CommuneLogo>

            {Configuration?.Filters?.Heading ? (
              <Heading size={100}>
                {Configuration.Filters.Heading.replace(
                  "{CommuneName}",
                  getCommuneName()
                )}
              </Heading>
            ) : null}
          </CommuneContainer>
        )}
      </Select>
    </div>
  );
};

export default ActivityMap;
